import React, { ReactElement } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch } from 'src/store/hooks'
import {
  updatePendingCEBrokerKentucky,
  updatePendingCEBrokerKentuckyCourse,
  fetchEventAccreditations,
  fetchCourseAccreditations,
} from 'src/v3/content-pipeline/accreditation.slice'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { StatusField } from '../shared'
import {
  CourseCodeField,
  ProfessionCodeField,
  SubjectAreaField,
  NotesField,
  DateField,
  FieldsRow,
} from './form-fields'
import {
  CEBrokerKentuckyPendingAccreditation,
  CEBrokerKentuckyFinalizedAccreditation,
} from './types'

type EditFormProps = {
  eventId?: string
  courseId?: string
  pending: CEBrokerKentuckyPendingAccreditation
  finalized?: CEBrokerKentuckyFinalizedAccreditation
  open: boolean
  setOpen: (isOpen: boolean) => void
  onSuccess: () => void
}

export let EditForm = ({
  eventId,
  courseId,
  pending,
  finalized,
  open,
  setOpen,
  onSuccess,
}: EditFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let form = useForm({
    defaultValues: {
      status: pending.status,
      courseCode: pending.courseCode || finalized?.courseCode || '',
      professionCode: pending.professionCode || finalized?.professionCode || '',
      subjectArea: pending.subjectArea || finalized?.subjectArea || '',
      expirationDate: pending.expirationDate || finalized?.expirationDate || '',
      notes: pending.notes || '',
    },
    onSubmit: ({ value }) => {
      let payload = {
        id: pending.id,
        status: value.status,
        courseCode: value.courseCode || null,
        professionCode: value.professionCode,
        subjectArea: value.subjectArea,
        state: 'Kentucky' as const,
        expirationDate: value.expirationDate || null,
        notes: value.notes || null,
      }
      if (courseId) {
        dispatch(updatePendingCEBrokerKentuckyCourse({ courseId, ...payload }))
        dispatch(fetchCourseAccreditations(courseId))
      } else if (eventId) {
        dispatch(updatePendingCEBrokerKentucky({ eventId, ...payload }))
        dispatch(fetchEventAccreditations(eventId))
      }
      setOpen(false)
      onSuccess()
    },
  })

  let handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.handleSubmit()
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <form onSubmit={handleFormSubmit}>
        <DialogTitle>Edit Pending CEBroker Kentucky Accreditation</DialogTitle>
        <DialogContent>
          <Box className="grid gap-6 mt-2">
            <Typography color="text.secondary" variant="body2">
              Update the pending CEBroker Kentucky accreditation details.
            </Typography>

            <form.Field
              name="status"
              children={(field) => <StatusField field={field} />}
            />

            <form.Field
              name="courseCode"
              children={(field) => <CourseCodeField field={field} />}
            />

            <FieldsRow>
              <form.Field
                name="professionCode"
                children={(field) => <ProfessionCodeField field={field} />}
              />
              <form.Field
                name="subjectArea"
                children={(field) => <SubjectAreaField field={field} />}
              />
            </FieldsRow>

            <form.Field
              name="expirationDate"
              children={(field) => (
                <DateField field={field} label="Expiration Date" />
              )}
            />

            <form.Field
              name="notes"
              children={(field) => <NotesField field={field} />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} type="button">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
